import { TCollabThread } from '@tiptap-pro/provider';
import React, { createContext, useCallback, useContext } from 'react';

interface ThreadsContextValue {
  threads: TCollabThread[];
  selectedThreads: string[];
  selectedThread: string | null;
  onClickThread: (threadId: string) => void;
  deleteThread: (threadId: string) => void;
  resolveThread: (threadId: string) => void;
  unresolveThread: (threadId: string) => void;
  onUpdateComment: (
    threadId: string,
    commentId: string,
    content: string,
    metaData: Record<string, unknown> | undefined
  ) => void;
  onHoverThread: (threadId: string) => void;
  onLeaveThread: () => void;
}

interface ThreadsProviderProps {
  children: React.ReactNode;
  threads: TCollabThread[];
  selectedThreads: string[];
  selectedThread: string | null;
  onClickThread: (threadId: string) => void;
  onDeleteThread: (threadId: string) => void;
  onResolveThread: (threadId: string) => void;
  onUnresolveThread: (threadId: string) => void;
  onUpdateComment: (
    threadId: string,
    commentId: string,
    content: string,
    metaData: Record<string, unknown> | undefined
  ) => void;
  onHoverThread: (threadId: string) => void;
  onLeaveThread: () => void;
  setSelectedThread: (threadId: string | ((currentThreadId: string | null) => string | null)) => void;
}

export const ThreadsContext = createContext<ThreadsContextValue>({
  threads: [],
  selectedThreads: [],
  selectedThread: null,
  onClickThread: () => null,
  deleteThread: () => null,
  resolveThread: () => null,
  unresolveThread: () => null,
  onUpdateComment: () => null,
  onHoverThread: () => null,
  onLeaveThread: () => null,
});

export const ThreadsProvider: React.FC<ThreadsProviderProps> = ({
  children,
  threads = EMPTY_THREADS,
  selectedThreads = EMPTY_SELECTED_THREADS,
  selectedThread = null,
  onClickThread = () => null,
  onDeleteThread = () => null,
  onResolveThread = () => null,
  onUnresolveThread = () => null,
  onUpdateComment = () => null,
  onHoverThread = () => null,
  onLeaveThread = () => null,
  setSelectedThread = () => null,
}) => {
  const handleThreadClick = useCallback(
    (threadId: string) => {
      setSelectedThread((currentThreadId: string | null) => {
        if (currentThreadId !== threadId) {
          onClickThread(threadId);
          setSelectedThread(threadId);
        }

        return currentThreadId !== threadId ? threadId : null;
      });
    },
    [onClickThread, setSelectedThread]
  );

  const providerValue: ThreadsContextValue = {
    threads,
    selectedThreads,
    selectedThread,
    deleteThread: onDeleteThread,
    resolveThread: onResolveThread,
    unresolveThread: onUnresolveThread,
    onClickThread: handleThreadClick,
    onUpdateComment,
    onHoverThread,
    onLeaveThread,
  };

  return <ThreadsContext.Provider value={providerValue}>{children}</ThreadsContext.Provider>;
};

export const useThreadsState = (): ThreadsContextValue => useContext(ThreadsContext);

const EMPTY_THREADS: TCollabThread[] = [];
const EMPTY_SELECTED_THREADS: string[] = [];
